"use client";

import * as React from "react";
import * as RSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "./cn";

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (v: string) => void;
  name?: string;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
};

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      name,
      placeholder,
      options,
      disabled,
      invalid,
      className,
      id,
    },
    ref,
  ) => (
    <RSelect.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      name={name}
      disabled={disabled}
    >
      <RSelect.Trigger
        id={id}
        ref={ref}
        className={cn(
          "group h-12 w-full rounded-[var(--radius-sm)] border-[1.5px]",
          "px-4 text-[15px] font-satoshi font-medium text-ink",
          "bg-mist border-smoke outline-none transition-all cursor-pointer",
          "focus:border-lime focus:bg-paper",
          "data-[state=open]:border-lime data-[state=open]:bg-paper",
          "disabled:cursor-not-allowed disabled:opacity-60",
          "flex items-center justify-between gap-2",
          invalid &&
            "border-danger focus:border-danger data-[state=open]:border-danger",
          className,
        )}
      >
        <RSelect.Value placeholder={placeholder} />
        <RSelect.Icon className="shrink-0 text-ash transition-transform duration-200 group-data-[state=open]:rotate-180">
          <ChevronDown size={18} strokeWidth={2} />
        </RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal>
        <RSelect.Content
          position="popper"
          sideOffset={6}
          className={cn(
            // Above Popover (z-70) so a Select used inside a popover panel —
            // e.g. the slot-create time picker — isn't trapped behind it. Stays
            // below tooltip/splash (z-100).
            "z-[80] w-[var(--radix-select-trigger-width)] overflow-hidden",
            "rounded-[var(--radius-md)] border border-smoke bg-paper",
            "shadow-[0_8px_24px_-4px_rgba(0,0,0,0.10),0_2px_8px_-2px_rgba(0,0,0,0.06)]",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "origin-top duration-150",
          )}
        >
          <RSelect.Viewport className="p-1.5 max-h-64">
            {options.map((opt) => (
              <RSelect.Item
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                className={cn(
                  "relative flex items-center h-10 px-3 rounded-[var(--radius-sm)]",
                  "text-[14px] font-medium text-ink outline-none cursor-pointer select-none",
                  "transition-colors duration-100",
                  "data-[highlighted]:bg-mist",
                  "data-[state=checked]:text-ember",
                  "data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed",
                )}
              >
                <RSelect.ItemText>{opt.label}</RSelect.ItemText>
                <RSelect.ItemIndicator className="ml-auto pl-2">
                  <Check size={14} strokeWidth={2.5} className="text-ember" />
                </RSelect.ItemIndicator>
              </RSelect.Item>
            ))}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  ),
);
Select.displayName = "Select";
